import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Edit2, Trash2, X, Save, Eye, MapPin, Truck } from 'lucide-react';
import apiService from '../../apiService';
import { useLiveRefresh } from '../../liveRefresh';
import CityFilter from '../../components/CityFilter';

const STATUS_COLORS = {
  pending:    { bg: '#fef9c3', color: '#ca8a04', label: 'En attente' },
  accepted:   { bg: '#FEE9E2', color: '#F54119', label: 'Acceptée' },
  preparing:  { bg: '#ffedd5', color: '#ea580c', label: 'Préparation' },
  ready:      { bg: '#cffafe', color: '#0891b2', label: 'Prête' },
  picked_up:  { bg: '#FEE9E2', color: '#C9350F', label: 'Récupérée' },
  delivering: { bg: '#f3e8ff', color: '#9333ea', label: 'En livraison' },
  delivered:  { bg: '#ECFDF5', color: '#10B981', label: 'Livrée' },
  cancelled:  { bg: '#fecaca', color: '#dc2626', label: 'Annulée' },
};

const PAYMENT_LABELS = {
  mobile_money: '📱 Mobile Money',
  cash: '💵 Cash',
  balance: '💰 Solde',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatShortDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Bloc PHARMACIE du détail commande : statut de l'ordonnance, retour de
// l'original, et consultation des photos (données de santé : chaque
// consultation est tracée au journal d'audit côté serveur).
const RX_STATUS = {
  pending_review: ['⏳ À examiner par le pharmacien', '#B45309', '#FEF3C7'],
  approved: ['✅ Ordonnance validée', '#047857', '#ECFDF5'],
  rejected: ['❌ Ordonnance refusée', '#B91C1C', '#FEE2E2'],
};
function PrescriptionBlock({ order }) {
  const [photos, setPhotos] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const show = async () => {
    setLoading(true); setErr('');
    try {
      const r = await apiService.get(`/pharmacy/admin/orders/${order.order_id}/prescriptions`, { userType: 'admin' });
      setPhotos(r.photos || []);
    } catch (e) { setErr(e.message || 'Accès refusé'); } finally { setLoading(false); }
  };
  const st = RX_STATUS[order.rx_status];
  return (
    <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '1rem', marginBottom: '1rem', border: '1px solid #E9EBEF' }}>
      <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#5C636E', marginBottom: '0.5rem' }}>📋 Pharmacie</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {st && <span style={{ background: st[2], color: st[1], borderRadius: 8, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>{st[0]}</span>}
        {order.quote_id && <span style={{ background: '#EFF6FF', color: '#1D4ED8', borderRadius: 8, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>Issue d'un devis</span>}
        {order.rx_original_return && (
          <span style={{ background: '#F4F5F7', color: '#414852', borderRadius: 8, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>
            Original : {order.rx_return_status === 'received' ? 'remis à la pharmacie ✅' : order.rx_return_status === 'to_return' ? 'retour en cours' : 'à récupérer'} · {(order.rx_return_fee || 0).toLocaleString()} FC
          </span>
        )}
      </div>
      {order.rx_rejection_reason && <div style={{ fontSize: '0.82rem', color: '#B91C1C', marginBottom: 8 }}>Motif du refus : {order.rx_rejection_reason}</div>}
      {photos === null ? (
        <button onClick={show} disabled={loading}
          style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #DCE0E6', background: 'white', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' }}>
          {loading ? 'Chargement…' : '🔒 Voir l\'ordonnance (consultation tracée)'}
        </button>
      ) : photos.length === 0 ? (
        <div style={{ fontSize: '0.82rem', color: '#9AA0A8' }}>Aucune photo (supprimée après la durée de conservation, ou commande sans photo).</div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {photos.map(p => p.url && (
            <a key={p.photo_id} href={p.url} target="_blank" rel="noreferrer">
              <img src={p.url} alt="ordonnance" style={{ width: 110, height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #E9EBEF' }} />
            </a>
          ))}
        </div>
      )}
      {err && <div style={{ fontSize: '0.8rem', color: '#B91C1C', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

export default function DeliveryOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [cityFilter, setCityFilter] = useState(''); // '' = toutes les villes
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editOrder, setEditOrder] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unblockingCodeType, setUnblockingCodeType] = useState('');

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (cityFilter) params.set('city_slug', cityFilter);
      params.set('page', page);
      params.set('limit', 20);
      const response = await apiService.get(`/admin/delivery/orders?${params}`, { userType: 'admin' });
      if (response.success) {
        setOrders(response.data || []);
        setTotal(response.total || 0);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, [search, status, cityFilter, page]);
  useLiveRefresh(() => fetchOrders(), ['delivery_order', 'delivery_blocked']);

  const handleDelete = async (order) => {
    if (!window.confirm(`Supprimer la commande #${order.order_number} ?\n\nCette action est irréversible.`)) return;
    try {
      const res = await apiService.delete(`/admin/delivery/orders/${order.order_id}`, { userType: 'admin' });
      if (res.success) {
        setOrders(prev => prev.filter(o => o.order_id !== order.order_id));
        setTotal(prev => prev - 1);
      }
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleSaveEdit = async () => {
    if (!editOrder) return;
    setSaving(true);
    try {
      const res = await apiService.put(`/admin/delivery/orders/${editOrder.order_id}`, {
        delivery_address: editOrder.delivery_address,
        delivery_details: editOrder.delivery_details,
        notes: editOrder.notes,
      }, { userType: 'admin' });
      // Statut : passe par l'action dédiée (remboursement, stock, règlements appliqués côté serveur)
      const before = orders.find(o => o.order_id === editOrder.order_id);
      if (res.success && editOrder.status && before && editOrder.status !== before.status) {
        if (editOrder.status === 'cancelled' && !window.confirm('Annuler cette commande ? Le client sera remboursé de ce qu\'il a payé d\'avance (moins 1 % de frais de service).')) {
          setSaving(false); return;
        }
        await apiService.patch(`/delivery/orders/${editOrder.order_id}/status`, {
          status: editOrder.status,
          reason: editOrder.status === 'cancelled' ? 'Annulée par l\'administration Mbiyo' : undefined,
        }, { userType: 'admin' });
      }
      if (res.success) {
        setEditOrder(null);
        fetchOrders();
      }
    } catch (err) {
      alert(err.message || 'Erreur lors de la mise à jour');
      fetchOrders();
    } finally { setSaving(false); }
  };

  const handleUnblockCode = async (codeType) => {
    if (!viewOrder?.order_id) return;
    setUnblockingCodeType(codeType);
    try {
      const res = await apiService.post(`/admin/delivery/orders/${viewOrder.order_id}/unblock-code`, {
        code_type: codeType,
      }, { userType: 'admin' });

      if (res.success) {
        const refreshed = await apiService.get(`/admin/delivery/orders?search=${viewOrder.order_id}&page=1&limit=1`, { userType: 'admin' });
        const updatedOrder = refreshed?.data?.find((o) => o.order_id === viewOrder.order_id);
        if (updatedOrder) {
          setViewOrder(updatedOrder);
        }
        fetchOrders();
      }
    } catch (err) {
      alert('Erreur lors du déblocage du code');
    } finally {
      setUnblockingCodeType('');
    }
  };

  const thStyle = { textAlign: 'left', padding: '0.75rem 0.75rem', fontWeight: '600', color: '#5C636E', fontSize: '0.8rem', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '0.65rem 0.75rem', fontSize: '0.85rem' };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#1E2229' }}>📦 Commandes Livraison</h2>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#9AA0A8' }}>Suivi de toutes les commandes ({total})</p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #E9EBEF', borderRadius: '12px', padding: '0.5rem 0.75rem' }}>
          <Search size={16} color="#9AA0A8" style={{ marginRight: '0.5rem' }} />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="N° commande, client, fournisseur..."
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.875rem', background: 'transparent' }} />
        </div>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
          style={{ background: 'white', border: '1px solid #E9EBEF', borderRadius: '12px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', cursor: 'pointer', outline: 'none' }}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_COLORS).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <CityFilter value={cityFilter} onChange={v => { setCityFilter(v); setPage(1); }} className="" style={{ background: 'white', border: '1px solid #E9EBEF', borderRadius: '12px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', cursor: 'pointer', outline: 'none' }} />
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#FFFFFF', borderBottom: '1px solid #E9EBEF' }}>
                <th style={thStyle}>N°</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Fournisseur</th>
                <th style={thStyle}>Livreur</th>
                <th style={thStyle}>Montant</th>
                <th style={thStyle}>Livraison</th>
                <th style={thStyle}>Statut</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const st = STATUS_COLORS[order.status] || { bg: '#F4F5F7', color: '#5C636E', label: order.status };
                return (
                  <tr key={order.order_id || order.id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                    <td style={{ ...tdStyle, fontWeight: '600', color: '#1E2229' }}>
                      <span style={{ fontSize: '0.78rem' }}>#{order.order_number}</span>
                    </td>
                    <td style={{ ...tdStyle, color: '#414852' }}>
                      <div>{order.client_name}</div>
                      {order.client_phone && <div style={{ fontSize: '0.72rem', color: '#9AA0A8' }}>{order.client_phone}</div>}
                    </td>
                    <td style={{ ...tdStyle, color: '#414852' }}>
                      <div>{order.supplier_name}</div>
                      {order.supplier_phone && <div style={{ fontSize: '0.72rem', color: '#9AA0A8' }}>{order.supplier_phone}</div>}
                    </td>
                    <td style={tdStyle}>
                      {order.driver_name ? (
                        <div>
                          <div style={{ color: '#414852' }}>{order.driver_name}</div>
                          {order.driver_phone && <div style={{ fontSize: '0.72rem', color: '#9AA0A8' }}>{order.driver_phone}</div>}
                        </div>
                      ) : (
                        <span style={{ padding: '0.15rem 0.5rem', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '600', background: '#fef3c7', color: '#FF9B00' }}>
                          ⏳ En attente
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>
                      <div>{(order.total || 0).toLocaleString()} FC</div>
                      <div style={{ fontSize: '0.72rem', color: '#9AA0A8', fontWeight: '400' }}>
                        dont {(order.delivery_fee || 0).toLocaleString()} livr.
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: '#5C636E', maxWidth: '160px' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem' }} title={order.delivery_address}>
                        📍 {order.delivery_address || '—'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', background: st.bg, color: st.color }}>{st.label}</span>
                    </td>
                    <td style={{ ...tdStyle, color: '#9AA0A8', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                      {formatShortDate(order.created_at)}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => setViewOrder(order)} title="Voir"
                          style={{ padding: '0.35rem', borderRadius: '6px', border: '1px solid #E9EBEF', background: '#FFFFFF', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Eye size={14} color="#F54119" />
                        </button>
                        <button onClick={() => setEditOrder({ ...order })} title="Modifier"
                          style={{ padding: '0.35rem', borderRadius: '6px', border: '1px solid #E9EBEF', background: '#FFFFFF', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Edit2 size={14} color="#FF7800" />
                        </button>
                        <button onClick={() => handleDelete(order)} title="Supprimer"
                          style={{ padding: '0.35rem', borderRadius: '6px', border: '1px solid #fecaca', background: '#fef2f2', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <Trash2 size={14} color="#DC2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && !loading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '3rem', color: '#9AA0A8' }}>Aucune commande</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', borderTop: '1px solid #E9EBEF' }}>
          <span style={{ fontSize: '0.875rem', color: '#9AA0A8' }}>Page {page} — {total} commande(s)</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #E9EBEF', background: 'white', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.3 : 1 }}>
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setPage(page + 1)} disabled={orders.length < 20}
              style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #E9EBEF', background: 'white', cursor: orders.length < 20 ? 'default' : 'pointer', opacity: orders.length < 20 ? 0.3 : 1 }}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ── VIEW MODAL ── */}
      {viewOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setViewOrder(null)}>
          <div style={{ background: 'white', borderRadius: '16px', maxWidth: '600px', width: '100%', maxHeight: '85vh', overflow: 'auto', padding: '1.5rem' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>📦 Commande #{viewOrder.order_number}</h3>
              <button onClick={() => setViewOrder(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#9AA0A8" /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <InfoBlock label="Statut" value={
                <span style={{ padding: '0.2rem 0.6rem', borderRadius: '10px', fontSize: '0.8rem', fontWeight: '600', background: STATUS_COLORS[viewOrder.status]?.bg, color: STATUS_COLORS[viewOrder.status]?.color }}>
                  {STATUS_COLORS[viewOrder.status]?.label || viewOrder.status}
                </span>
              } />
              <InfoBlock label="Paiement" value={PAYMENT_LABELS[viewOrder.payment_method] || viewOrder.payment_method} />
              <InfoBlock label="👤 Client" value={`${viewOrder.client_name}${viewOrder.client_phone ? ' — ' + viewOrder.client_phone : ''}`} />
              <InfoBlock label="🏪 Fournisseur" value={`${viewOrder.supplier_name}${viewOrder.supplier_phone ? ' — ' + viewOrder.supplier_phone : ''}`} />
              <InfoBlock label="🏍️ Livreur" value={viewOrder.driver_name ? `${viewOrder.driver_name}${viewOrder.driver_phone ? ' — ' + viewOrder.driver_phone : ''}` : '⏳ En attente'} />
              <InfoBlock label="📅 Créée" value={formatDate(viewOrder.created_at)} />
            </div>

            <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#5C636E', marginBottom: '0.5rem' }}>📍 Adresse de livraison</div>
              <div style={{ fontSize: '0.9rem', color: '#1E2229' }}>{viewOrder.delivery_address || '—'}</div>
              {viewOrder.delivery_details && <div style={{ fontSize: '0.8rem', color: '#5C636E', marginTop: '0.25rem' }}>{viewOrder.delivery_details}</div>}
            </div>

            <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#5C636E', marginBottom: '0.5rem' }}>🛒 Articles ({viewOrder.items_count})</div>
              {(viewOrder.items || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: i < viewOrder.items.length - 1 ? '1px solid #E9EBEF' : 'none' }}>
                  <span style={{ fontSize: '0.85rem', color: '#414852' }}>{item.quantity}× {item.product_name}</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1E2229' }}>{(item.total_price || 0).toLocaleString()} FC</span>
                </div>
              ))}
            </div>

            {viewOrder.is_pharmacy && (viewOrder.rx_required || viewOrder.rx_original_return) && (
              <PrescriptionBlock order={viewOrder} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <InfoBlock label="Sous-total" value={`${(viewOrder.subtotal || 0).toLocaleString()} FC`} />
              <InfoBlock label="Frais livraison" value={`${(viewOrder.delivery_fee || 0).toLocaleString()} FC`} />
              <InfoBlock label="Total" value={<span style={{ fontWeight: '700', color: '#F54119', fontSize: '1rem' }}>{(viewOrder.total || 0).toLocaleString()} FC</span>} />
            </div>

            <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#5C636E', marginBottom: '0.7rem' }}>🔐 Vérification codes</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div style={{ border: '1px solid #E9EBEF', borderRadius: '10px', padding: '0.75rem', background: '#fff' }}>
                  <div style={{ fontSize: '0.75rem', color: '#5C636E', marginBottom: '0.2rem' }}>Code pickup</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: '700', letterSpacing: '0.08em', color: '#12151A' }}>{viewOrder.pickup_code || '—'}</div>
                  <div style={{ fontSize: '0.78rem', color: viewOrder.pickup_code_verified ? '#10B981' : '#5C636E', marginTop: '0.3rem' }}>
                    {viewOrder.pickup_code_verified ? '✅ Vérifié' : `Tentatives: ${viewOrder.pickup_code_attempts || 0}/3`}
                  </div>
                  {viewOrder.pickup_blocked_at && (
                    <>
                      <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.25rem' }}>🚫 Bloqué le {formatDate(viewOrder.pickup_blocked_at)}</div>
                      <button
                        onClick={() => handleUnblockCode('pickup')}
                        disabled={unblockingCodeType === 'pickup'}
                        style={{ marginTop: '0.5rem', width: '100%', padding: '0.45rem 0.6rem', borderRadius: '8px', border: '1px solid #fecaca', background: '#fef2f2', color: '#B91C1C', fontWeight: '600', cursor: 'pointer', opacity: unblockingCodeType === 'pickup' ? 0.6 : 1 }}
                      >
                        {unblockingCodeType === 'pickup' ? 'Déblocage...' : 'Débloquer code pickup'}
                      </button>
                    </>
                  )}
                </div>

                <div style={{ border: '1px solid #E9EBEF', borderRadius: '10px', padding: '0.75rem', background: '#fff' }}>
                  <div style={{ fontSize: '0.75rem', color: '#5C636E', marginBottom: '0.2rem' }}>Code livraison</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: '700', letterSpacing: '0.08em', color: '#12151A' }}>{viewOrder.delivery_code || '—'}</div>
                  <div style={{ fontSize: '0.78rem', color: viewOrder.delivery_code_verified ? '#10B981' : '#5C636E', marginTop: '0.3rem' }}>
                    {viewOrder.delivery_code_verified ? '✅ Vérifié' : `Tentatives: ${viewOrder.delivery_code_attempts || 0}/3`}
                  </div>
                  {viewOrder.delivery_blocked_at && (
                    <>
                      <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.25rem' }}>🚫 Bloqué le {formatDate(viewOrder.delivery_blocked_at)}</div>
                      <button
                        onClick={() => handleUnblockCode('delivery')}
                        disabled={unblockingCodeType === 'delivery'}
                        style={{ marginTop: '0.5rem', width: '100%', padding: '0.45rem 0.6rem', borderRadius: '8px', border: '1px solid #fecaca', background: '#fef2f2', color: '#B91C1C', fontWeight: '600', cursor: 'pointer', opacity: unblockingCodeType === 'delivery' ? 0.6 : 1 }}
                      >
                        {unblockingCodeType === 'delivery' ? 'Déblocage...' : 'Débloquer code livraison'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#5C636E', marginBottom: '0.5rem' }}>📅 Chronologie</div>
              {[
                { label: 'Créée', date: viewOrder.created_at },
                { label: 'Acceptée', date: viewOrder.accepted_at },
                { label: 'En préparation', date: viewOrder.preparing_at },
                { label: 'Prête', date: viewOrder.ready_at },
                { label: 'Récupérée', date: viewOrder.picked_up_at },
                { label: 'Livrée', date: viewOrder.delivered_at },
                { label: 'Annulée', date: viewOrder.cancelled_at },
              ].filter(t => t.date).map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.8rem' }}>
                  <span style={{ color: '#5C636E' }}>{t.label}</span>
                  <span style={{ color: '#1E2229', fontWeight: '500' }}>{formatDate(t.date)}</span>
                </div>
              ))}
            </div>

            {viewOrder.notes && (
              <div style={{ background: '#fffbeb', borderRadius: '10px', padding: '0.75rem', marginTop: '1rem', fontSize: '0.85rem', color: '#C9350F' }}>
                📝 <strong>Notes :</strong> {viewOrder.notes}
              </div>
            )}
            {viewOrder.cancellation_reason && (
              <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '0.75rem', marginTop: '0.5rem', fontSize: '0.85rem', color: '#991b1b' }}>
                ❌ <strong>Raison annulation :</strong> {viewOrder.cancellation_reason}
              </div>
            )}
            {viewOrder.whatsapp?.alert_sent_at && (
              <div style={{ background: '#ecfdf5', borderRadius: '10px', padding: '0.75rem', marginTop: '0.5rem', fontSize: '0.85rem', color: '#065f46' }}>
                💬 <strong>WhatsApp :</strong> alerte envoyée au commerce{viewOrder.whatsapp.reminder_sent_at ? ' (+ rappel)' : ''}
                {viewOrder.whatsapp.last_action ? ` · dernière action depuis WhatsApp : ${viewOrder.whatsapp.last_action}` : ' · aucune action depuis WhatsApp'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      {editOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setEditOrder(null)}>
          <div style={{ background: 'white', borderRadius: '16px', maxWidth: '500px', width: '100%', maxHeight: '85vh', overflow: 'auto', padding: '1.5rem' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>✏️ Modifier #{editOrder.order_number}</h3>
              <button onClick={() => setEditOrder(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#9AA0A8" /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <EditField label="Statut">
                <select value={editOrder.status} onChange={e => setEditOrder({ ...editOrder, status: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #E9EBEF', fontSize: '0.875rem', outline: 'none' }}>
                  {Object.entries(STATUS_COLORS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </EditField>

              <EditField label="📍 Adresse de livraison">
                <input value={editOrder.delivery_address} onChange={e => setEditOrder({ ...editOrder, delivery_address: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #E9EBEF', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
              </EditField>

              <EditField label="Détails livraison">
                <input value={editOrder.delivery_details} onChange={e => setEditOrder({ ...editOrder, delivery_details: e.target.value })}
                  placeholder="Bâtiment, étage, repère..."
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #E9EBEF', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
              </EditField>

              <EditField label="📝 Notes">
                <textarea value={editOrder.notes} onChange={e => setEditOrder({ ...editOrder, notes: e.target.value })}
                  rows={3}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #E9EBEF', fontSize: '0.875rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </EditField>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditOrder(null)}
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #E9EBEF', background: 'white', fontSize: '0.875rem', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={handleSaveEdit} disabled={saving}
                style={{ padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none', background: '#FF7800', color: 'white', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#9AA0A8', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.2rem' }}>{label}</div>
      <div style={{ fontSize: '0.875rem', color: '#1E2229' }}>{value}</div>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#414852', marginBottom: '0.3rem' }}>{label}</label>
      {children}
    </div>
  );
}
